// src/contexts/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from "react";

import {
  loadInitialSession,
  checkSubscriptionStatusService,
} from "@/services/auth/authSession";

import {
  loginService,
  createUserAuthService,
  completeRegistrationService,
  logoutService,
  updateOrganizationService,
  deleteCurrentAccount,
} from "@/services/auth/authAccount";

import {
  registerWithTokenService,
  resendConfirmationEmailService,
  completeOnboardingAfterConfirmationService,
} from "@/services/auth/authInvite";

import {
  changePasswordService,
  forgotPasswordService,
  resetPasswordService,
} from "@/services/auth/authPassword";

import { createCheckoutSessionService } from "@/services/auth/authBilling";
import {
  fetchShopTrophies,
  createShopTrophy,
  updateShopTrophy,
  deleteShopTrophy,
  fetchShopTrophiesProgress,
  ShopTrophy,
  ShopTrophyProgress
} from "@/services/shop/shopTrophies";
import {
  getBoostedShops,
  ShopVisibility
} from "@/services/shop/shopVisibility";

// ---------------------------
// Types
// ---------------------------

type AuthUser = any;
type Organization = any;

export type AuthResult = {
  success: boolean;
  error: string;
  redirectTo?: string;
};

export type CheckoutResult = {
  success: boolean;
  error: string;
  url?: string;
};

export interface AuthContextValue {
  /** Account "effettivo": il dipendente impersonato se la vista è attiva, altrimenti l'account loggato. */
  user: AuthUser | null;
  /** Account realmente loggato (sessione Supabase), anche durante "Vedi come". */
  realUser: AuthUser | null;
  /** Dipendente impersonato con "Vedi come" (null = nessuna vista attiva). */
  viewAsUser: AuthUser | null;
  /** Attiva la vista "Vedi come" su un dipendente (solo titolare). */
  startViewAs: (staff: any) => void;
  /** Torna all'account reale. */
  stopViewAs: () => void;
  isAdmin: boolean
  /** Livello permessi effettivo dell'utente corrente (owner=titolare, senior, junior). */
  permissionLevel: 'owner' | 'senior' | 'junior';
  /** true solo per il titolare (role=admin). */
  isOwner: boolean;
  /** Può vedere l'agenda di tutto lo staff (owner+senior). Junior vede solo la propria. */
  canSeeAllAgenda: boolean;
  /** Può vedere i numeri finanziari (solo owner). */
  canSeeFinancials: boolean;
  /** Può accedere alle analytics (owner+senior). Junior no. */
  canSeeAnalytics: boolean;
  organization: Organization | null;
  loading: boolean;
  isAuthenticated: boolean;
  isPaid: boolean;
  subscriptionPlan: string | null;
  recoveryToken: string | null;

  login: (credentials: {
    email: string;
    password: string;
    inviteToken?: string;
  }) => Promise<AuthResult>;

  createUserAuth: (userData: any) => Promise<AuthResult>;

  /** OAuth login (Google, Apple) */
  loginWithProvider: (provider: "google" | "apple") => Promise<AuthResult>;

  completeRegistration: (
    userData: any
  ) => Promise<{ success: boolean; error: string; shopData?: any }>;

  logout: () => Promise<void>;

  deleteAccount: () => Promise<{ success: boolean; error: string }>;

  updateOrganization: (
    data: any
  ) => Promise<{ success: boolean; error: string }>;

  changePassword: (
    current: string,
    next: string
  ) => Promise<{ success: boolean; error: string }>;

  registerWithToken: (data: any, token: string) => Promise<AuthResult>;

  createCheckoutSession: (
    plan?: string,
    data?: any
  ) => Promise<CheckoutResult>;

  checkSubscriptionStatus: (
    shopId: string | number | null | undefined
  ) => Promise<void>;

  forgotPassword: (email: string) => Promise<{ success: boolean; error: string }>;

  resetPassword: (password: string) => Promise<{ success: boolean; error: string }>;

  setRecoveryToken: (token: string | null) => void;

  updateCurrentUser: (data: any) => void;

  resendConfirmationEmail: () => Promise<{ success: boolean; error: string }>;

  completeOnboardingAfterConfirmation: () => Promise<{
    success: boolean;
    error: string;
  }>;

  // Trophies Management
  trophies: ShopTrophy[];
  trophiesProgress: ShopTrophyProgress[];
  trophiesLoading: boolean;
  loadShopTrophies: () => Promise<void>;
  addTrophy: (data: Omit<ShopTrophy, 'id' | 'shop_id'>) => Promise<void>;
  updateTrophyItem: (id: number, data: Partial<ShopTrophy>) => Promise<void>;
  deleteTrophyItem: (id: number) => Promise<void>;

  // Visibility Management
  visibility: ShopVisibility | null;
  loadShopVisibility: () => Promise<void>;
}

// ---------------------------
// Context Setup
// ---------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);

  // Trophies State
  const [trophies, setTrophies] = useState<ShopTrophy[]>([]);
  const [trophiesProgress, setTrophiesProgress] = useState<ShopTrophyProgress[]>([]);
  const [trophiesLoading, setTrophiesLoading] = useState(false);

  // Visibility State
  const [visibility, setVisibility] = useState<ShopVisibility | null>(null);

  // "Vedi come" (impersonate esplicito, solo titolare): vista client-side sulla prospettiva
  // di un dipendente. Non tocca la sessione Supabase (RLS resta quella dell'account reale)
  // e non sopravvive al reload della pagina.
  const [viewAsUser, setViewAsUser] = useState<AuthUser | null>(null);
  const effectiveUser = viewAsUser ?? user;

  const startViewAs = (staff: any) => {
    if (!user || (user as any).role !== 'admin') return;
    if (!staff || staff.id === (user as any).id) return;
    setViewAsUser(staff);
  };
  const stopViewAs = () => setViewAsUser(null);

  const isAdmin = useMemo(() => {
    if (!effectiveUser) return false;
    return effectiveUser.role === 'admin';
  }, [effectiveUser]);

  // Livello permessi (3 tier). role=admin => owner; altrimenti permission_level (senior) o junior.
  const permissionLevel = useMemo<'owner' | 'senior' | 'junior'>(() => {
    if (!effectiveUser) return 'junior';
    if ((effectiveUser as any).role === 'admin') return 'owner';
    return (effectiveUser as any).permission_level === 'senior' ? 'senior' : 'junior';
  }, [effectiveUser]);
  const isOwner = permissionLevel === 'owner';
  const canSeeAllAgenda = permissionLevel !== 'junior';
  const canSeeFinancials = isOwner;
  // Analytics è un cruscotto finanziario → riservato al titolare.
  const canSeeAnalytics = isOwner;

  // Load initial session
  useEffect(() => {
    void loadInitialSession({
      setUser,
      setOrganization,
      setIsAuthenticated,
      setIsPaid,
      setSubscriptionPlan,
      setLoading,
      setSubscriptionLoading,
    });
  }, []);

  // Load trophies and visibility when organization is available
  useEffect(() => {
    if (organization?.id) {
      void loadShopTrophies();
      void loadShopVisibility();
    } else {
      setTrophies([]);
      setVisibility(null);
    }
  }, [organization]);

  // ------------------------
  // AUTH ACTIONS
  // ------------------------

  const login = (credentials: { email: string; password: string; inviteToken?: string }) =>
    loginService(credentials, {
      setUser,
      setOrganization,
      setIsAuthenticated,
      setIsPaid,
      setSubscriptionPlan,
      setLoading: () => { },
      setSubscriptionLoading,
    });

  const createUserAuth = (userData: any) =>
    createUserAuthService(userData, {
      setUser,
      setIsAuthenticated,
      setLoading: () => { },
    });

  const completeRegistration = (userData: any) =>
    completeRegistrationService(userData, {
      user,
      setUser,
      setOrganization,
      setIsAuthenticated,
      setLoading: () => { },
    });

  const logout = () => {
    setViewAsUser(null);
    return logoutService({
      setUser,
      setOrganization,
      setIsAuthenticated,
      setIsPaid,
    });
  };

  const deleteAccount = async (): Promise<{ success: boolean; error: string }> => {
    try {
      if (!user?.id) throw new Error("Utente non autenticato");

      // 1. Chiama il servizio per l'eliminazione
      await deleteCurrentAccount(user.id, isPaid);

      // 2. Se l'eliminazione ha successo, esegui il logout completo
      await logout();

      return { success: true, error: "" };
    } catch (err: any) {
      // Se fallisce l'eliminazione, NON facciamo logout, così l'utente può riprovare o vedere l'errore
      return {
        success: false,
        error: err.message || "Errore durante l'eliminazione dell'account"
      };
    }
  };

  // ------------------------
  // OAUTH LOGIN (Google, Apple)
  // ------------------------

  const loginWithProvider = async (
    provider: "google" | "apple"
  ): Promise<AuthResult> => {
    try {
      const { supabase } = await import("@/lib/supabase");

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?redirect_type=oauth`,
        }
      });

      if (error) throw new Error("Errore login OAuth");

      return { success: true, error: "" };
    } catch (err: any) {
      return { success: false, error: err?.message ?? "Errore OAuth" };
    }
  };

  // ------------------------
  // OTHER METHODS
  // ------------------------

  const registerWithToken = (userData: any, token: string) =>
    registerWithTokenService(userData, token, {
      setIsAuthenticated,
      setLoading: () => { },
      setUser,
      setOrganization,
      setIsPaid,
      setSubscriptionPlan,
      setSubscriptionLoading,
    });

  const updateOrganization = (organizationData: any) =>
    updateOrganizationService(organizationData, {
      organization,
      setOrganization,
    });

  const changePassword = (currentPassword: string, newPassword: string) =>
    changePasswordService(currentPassword, newPassword, { user });

  const forgotPassword = (email: string) => forgotPasswordService(email);

  const resetPassword = (newPassword: string) =>
    resetPasswordService(newPassword, { setRecoveryToken });

  const createCheckoutSession = (plan?: string, data?: any) =>
    createCheckoutSessionService(plan, data ?? organization);

  const updateCurrentUser = (userData: any) => {
    // Durante "Vedi come" gli aggiornamenti del profilo riguardano il dipendente
    // impersonato: aggiornare la vista, MAI sovrascrivere l'account reale.
    if (viewAsUser && userData?.id === (viewAsUser as any).id) {
      setViewAsUser(userData);
      return;
    }
    setUser(userData);
  };

  const resendConfirmationEmail = () => resendConfirmationEmailService();

  const completeOnboardingAfterConfirmation = () =>
    completeOnboardingAfterConfirmationService({
      user,
      setUser,
      setOrganization,
      setIsAuthenticated,
      setIsPaid,
      setSubscriptionPlan,
      setLoading,
      setSubscriptionLoading,
    });

  const checkSubscriptionStatus = async (
    shopId: string | number | null | undefined
  ) =>
    checkSubscriptionStatusService(shopId, {
      setIsPaid,
      setSubscriptionPlan,
      setSubscriptionLoading,
    });

  // ------------------------
  // TROPHIES METHODS
  // ------------------------

  const loadShopTrophies = async () => {
    if (!organization?.id) return;
    setTrophiesLoading(true);
    try {
      const [trophiesData, progressData] = await Promise.all([
        fetchShopTrophies(organization.id),
        fetchShopTrophiesProgress(organization.id)
      ]);

      setTrophies(trophiesData);
      setTrophiesProgress(progressData);
    } catch (error) {
      console.error("Error loading trophies data", error);
    } finally {
      setTrophiesLoading(false);
    }
  };

  const addTrophy = async (data: Omit<ShopTrophy, 'id' | 'shop_id'>) => {
    if (!organization?.id) return;
    try {
      const newTrophy = await createShopTrophy({
        shop_id: organization.id,
        ...data
      });
      // Aggiorna lo stato locale
      setTrophies(prev => [...prev, newTrophy]);
    } catch (error) {
      throw error; // Rilancia per gestire nel componente
    }
  };

  const updateTrophyItem = async (id: number, data: Partial<ShopTrophy>) => {
    try {
      const updated = await updateShopTrophy(id, data);
      setTrophies(prev => prev.map(t => t.id === id ? updated : t));
    } catch (error) {
      throw error;
    }
  };

  const deleteTrophyItem = async (id: number) => {
    try {
      await deleteShopTrophy(id);
      setTrophies(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      throw error;
    }
  };

  // ------------------------
  // VISIBILITY METHODS
  // ------------------------

  const loadShopVisibility = async () => {
    if (!organization?.id) return;
    try {
      const data = await getBoostedShops(organization.id);
      setVisibility(data);
    } catch (error) {
      console.error("Error loading visibility data", error);
    }
  };

  // ------------------------
  // CONTEXT VALUE
  // ------------------------

  const value: AuthContextValue = {
    user: effectiveUser,
    realUser: user,
    viewAsUser,
    startViewAs,
    stopViewAs,
    isAdmin,
    permissionLevel,
    isOwner,
    canSeeAllAgenda,
    canSeeFinancials,
    canSeeAnalytics,
    organization,
    loading: loading || subscriptionLoading,
    isAuthenticated,
    isPaid,
    subscriptionPlan,
    recoveryToken,
    trophies,
    trophiesProgress,
    trophiesLoading,
    login,
    createUserAuth,
    loginWithProvider,
    completeRegistration,
    logout,
    deleteAccount,
    updateOrganization,
    changePassword,
    registerWithToken,
    createCheckoutSession,
    checkSubscriptionStatus,
    forgotPassword,
    resetPassword,
    setRecoveryToken,
    updateCurrentUser,
    resendConfirmationEmail,
    completeOnboardingAfterConfirmation,
    loadShopTrophies,
    addTrophy,
    updateTrophyItem,
    deleteTrophyItem,
    visibility,
    loadShopVisibility,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};